import { useMemo } from 'react';
import { FolderItemRepresentation, FolderItemType } from '@common/api';
import { tail } from '@util/array';
import { pagePath } from '@pages/pagePaths';
import { useNavigateWithParams } from '@hooks/useNavigateWithParams';
import { useNavigationContext } from '@providers/NavigationProvider';

export interface OpenFolderItemInfo {
    path: number[];
    allowedTypes: FolderItemType[];
    type: FolderItemType;
    id: number;
    reference: number | null;
}

export function useOpenFolderItemReference() {
    const navigate = useNavigateWithParams();
    const { getLastPath } = useNavigationContext();

    return useMemo(
        () => ({
            openFolderItemReference(rep: FolderItemRepresentation | OpenFolderItemInfo) {
                const f = rep as OpenFolderItemInfo;
                const { path } = f;

                const parentId = tail(path);
                const isVariableAllowed = f.allowedTypes?.includes(FolderItemType.Variable);

                switch (f.type) {
                    case FolderItemType.Dashboard:
                        navigate(`${pagePath.Dashboard}/${f.reference}`);
                        break;
                    case FolderItemType.DimensionTable:
                        navigate(`${pagePath.Database}/${f.reference}`);
                        break;
                    case FolderItemType.Variable:
                        navigate(`${pagePath.Variables}/${parentId}/${f.reference}`);
                        break;
                    case FolderItemType.Sheet: {
                        const lastPath = getLastPath(Number(f.reference)) ?? f.reference;
                        navigate(`${pagePath.Sheet}/${f.reference}/${lastPath}`);
                        break;
                    }
                    case FolderItemType.FarseerFile:
                        navigate(`${pagePath.Files}/${f.reference}`);
                        break;
                    case FolderItemType.RemoteJob:
                        navigate(`${pagePath.RemoteJobs}/${f.reference}`);
                        break;
                    case FolderItemType.Folder: {
                        if (isVariableAllowed) {
                            navigate(`${pagePath.Variables}/${f.id}`);
                            break;
                        }

                        navigate(`${pagePath.Folder}/${f.id}`);
                        break;
                    }
                    default:
                        break;
                }
            },
        }),
        [navigate, getLastPath]
    );
}
